
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Trash2, ArrowUpDown, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CustomFormField } from '@/components/ui/custom-form-field';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { serviceCategories } from '@/data/serviceCategories';

const ServiceManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  
  // Extract all services from service categories
  const allServices = serviceCategories.flatMap(category => 
    category.services.map(service => ({
      ...service,
      categoryId: category.id,
      categoryTitle: category.title
    }))
  );
  
  const filteredServices = searchQuery 
    ? allServices.filter(service => 
        service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.categoryTitle.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allServices;
    
  const form = useForm({
    defaultValues: {
      name: '',
      path: '',
      category: ''
    }
  });
  
  const onSubmit = (data: any) => {
    console.log('Service form submitted:', data);
    setIsDialogOpen(false);
    form.reset();
  };
  
  const handleEditService = (service: any) => {
    setSelectedService(service);
    form.reset({
      name: service.name,
      path: service.path,
      category: service.categoryId
    });
    setIsDialogOpen(true);
  };
  
  const handleAddNewService = () => {
    setSelectedService(null);
    form.reset({
      name: '',
      path: '',
      category: ''
    });
    setIsDialogOpen(true);
  };
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold">Service Management</h2>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-grow sm:flex-grow-0">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search services..."
              className="pl-8 w-full sm:w-[200px]"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddNewService}>
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{selectedService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                <DialogDescription>
                  {selectedService 
                    ? 'Update the details for this service.' 
                    : 'Add a new service to your offerings.'}
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <CustomFormField
                    form={form}
                    name="name"
                    label="Service Name"
                    placeholder="e.g., Content Marketing"
                    required
                  />
                  
                  <CustomFormField
                    form={form}
                    name="path"
                    label="URL Path"
                    placeholder="e.g., /services/content-marketing"
                    required
                  />
                  
                  <div className="grid w-full max-w-sm items-center gap-1.5">
                    <label htmlFor="category" className="text-sm font-medium">Category</label>
                    <select
                      id="category"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-foreground file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      {...form.register('category')}
                    >
                      <option value="">Select a category</option>
                      {serviceCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <DialogFooter>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      {selectedService ? 'Update Service' : 'Add Service'}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <Card>
        <div className="p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <div className="flex items-center">
                    Service Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Category</TableHead>
                <TableHead>URL Path</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServices.length > 0 ? (
                filteredServices.map((service, index) => (
                  <TableRow key={`${service.categoryId}-${index}`}>
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell>{service.categoryTitle}</TableCell>
                    <TableCell className="font-mono text-xs">{service.path}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleEditService(service)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6">
                    No services found matching your search criteria
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default ServiceManagement;
